'use client'
import { createContext, useContext, useState, useEffect } from "react";
import { fetchUserProfile } from "@/API/Account/accountAPI";

const UserContext = createContext(null);

/**
 * @param children - The child components that will have access to the user context.
 * @description The UserProvider component provides user-related state and functions to its child components. It manages the user's login status, username, and linked account providers.
 * @returns The UserProvider component that wraps its children with the UserContext.Provider.
 * It provides the following values to its children:
 * - username: The current user's username.
 * - isLoggedIn: A boolean indicating whether the user is logged in.
 * - linkedProviders: An object indicating which third-party providers are linked to the user's account (e.g., { google: true }).
 * - setUsername: Function to update the username state.
 * - setIsLoggedIn: Function to update the login status state.
 * - setLinkedProviders: Function to update the linked providers state.
 * - refreshProfile: Function to fetch and refresh the user's profile information from the backend.
 */
export function UserProvider({ children }) {
  const [username, setUsername] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [linkedProviders, setLinkedProviders] = useState({ google: false });

  const refreshProfile = () => {
    const controller = new AbortController();
    fetchUserProfile(controller.signal)
      .then((res) => {
        if (res && res.success) {
          setUsername(res.username);
          setIsLoggedIn(true);
          setLinkedProviders(res.linkedAccounts || { google: false });
        } else {
          setIsLoggedIn(false);
          setUsername("");
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setIsLoggedIn(false);
          setUsername("");
        }
      });
    return () => controller.abort();
  };

  useEffect(() => {
    return refreshProfile();
  }, []);

  return (
    <UserContext.Provider
      value={{
        username,
        isLoggedIn,
        linkedProviders,
        setUsername,
        setIsLoggedIn,
        setLinkedProviders,
        refreshProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

/**
 * @description Custom hook to access the user context for other components. It provides the current user's information and functions to update it.
 * It must be used within a UserProvider component.
 * @throws Will throw an error if used outside of a UserProvider.
 * @returns The user context values.
 */
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}